
import React from 'react';
import { Session, Journey, StrategicPlan } from '@shared/types';
import { SCORING_DOMAINS } from '../constants';

interface Props {
  sessions: Session[];
  journeys: Journey[];
  strategicPlan: StrategicPlan | null;
  updatingPlan: boolean;
  onGeneratePlan: () => void;
  onBack: () => void;
}

const Insights: React.FC<Props> = ({ sessions, journeys, strategicPlan, updatingPlan, onGeneratePlan, onBack }) => {
  // Sadece kullanıcının kendi sessions'ları için analytics hesapla (public olanları hariç)
  const userSessions = sessions.filter(s => !s.isPublic);
  const completedSessions = userSessions.filter(s => s.attempts.length > 0).sort((a, b) =>
    new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
  );

  const totalScores = completedSessions.reduce((acc, s) => {
    const sessionAvg = s.attempts.reduce((a, att) => a + (att.feedback?.overallScore || 0), 0) / (s.attempts.length || 1);
    return acc + sessionAvg;
  }, 0);
  const avgScore = completedSessions.length > 0 ? Math.round(totalScores / completedSessions.length) : 0;

  // Frequency-based Blind Spot Analysis
  const blindSpotCounts: Record<string, number> = {};
  completedSessions.forEach(s => {
    s.attempts.forEach(a => {
      a.feedback?.blindSpots?.forEach(spot => {
        blindSpotCounts[spot] = (blindSpotCounts[spot] || 0) + 1;
      });
    });
  });

  const topBlindSpots = Object.entries(blindSpotCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);

  // Skill Matrix Calculation
  const domainAverages = SCORING_DOMAINS.reduce((acc, domain) => {
    let sum = 0, count = 0;
    completedSessions.forEach(s => {
      s.attempts.forEach(a => {
        const score = (a.feedback?.scores as any)?.[domain];
        if (score !== undefined) {
          sum += score;
          count++;
        }
      });
    });
    acc[domain] = count > 0 ? Math.round(sum / count) : 0;
    return acc;
  }, {} as Record<string, number>);

  // Trend Analysis
  const lastFiveTrends = completedSessions.slice(-5).map(s => {
    const avg = s.attempts.reduce((a, att) => a + (att.feedback?.overallScore || 0), 0) / (s.attempts.length || 1);
    return avg;
  });

  const isImproving = lastFiveTrends.length > 1 && lastFiveTrends[lastFiveTrends.length - 1] >= lastFiveTrends[0];

  return (
    <div className="p-8 lg:p-12 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-16 print:mb-6">
        <div>
          <h1 className="text-4xl font-black text-slate-900 mb-2 leading-none">Career Analytics</h1>
          <p className="text-slate-500 font-medium italic">Benchmarking clinical performance against professional medical standards.</p>
        </div>
        <div className="flex gap-4 print:hidden">
          <button onClick={() => window.print()} className="bg-white border border-slate-200 text-slate-700 px-8 py-3 rounded-2xl text-sm font-bold shadow-sm hover:bg-slate-50 transition-all">Export Portfolio Report</button>
          <button onClick={onBack} className="bg-slate-900 text-white px-8 py-3 rounded-2xl text-sm font-bold shadow-sm hover:bg-blue-600 transition-all">Back</button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-16">
        <div className="bg-white border border-slate-100 p-10 rounded-[3rem] shadow-xl shadow-slate-100/50 text-center group">
          <p className="text-[10px] font-black text-slate-400 uppercase mb-3 tracking-widest">Aggregate Mastery</p>
          <div className="flex flex-col items-center">
             <h3 className="text-6xl font-black text-blue-600 group-hover:scale-110 transition-transform">{avgScore}<span className="text-slate-200 text-2xl font-medium">%</span></h3>
             {completedSessions.length > 1 && (
               <span className={`text-[10px] font-black uppercase mt-2 ${isImproving ? 'text-emerald-500' : 'text-amber-500'}`}>
                 {isImproving ? '↑ Trending Up' : '↓ Stagnating'}
               </span>
             )}
          </div>
        </div>
        <div className="bg-white border border-slate-100 p-10 rounded-[3rem] shadow-xl shadow-slate-100/50 text-center">
          <p className="text-[10px] font-black text-slate-400 uppercase mb-3 tracking-widest">Total Stations Run</p>
          <h3 className="text-6xl font-black text-slate-800">{userSessions.reduce((acc, s) => acc + s.attempts.length, 0)}</h3>
        </div>
        <div className="bg-amber-50 border border-amber-100 p-10 rounded-[3rem] shadow-xl shadow-amber-100/50 text-center">
          <p className="text-[10px] font-black text-amber-600 uppercase mb-3 tracking-widest">Unique Critical Gaps</p>
          <h3 className="text-6xl font-black text-amber-600">{Object.keys(blindSpotCounts).length}</h3>
        </div>
        <div className="bg-slate-900 p-10 rounded-[3rem] shadow-2xl text-center relative overflow-hidden flex flex-col justify-center">
          <div className="absolute top-0 right-0 w-16 h-16 bg-blue-600/20 blur-xl"></div>
          <p className="text-blue-400 text-[10px] font-black uppercase mb-3 tracking-widest">Global Ranking Status</p>
          <h3 className="text-2xl font-black text-white mt-2 uppercase tracking-tight">
            {avgScore >= 85 ? 'Distinction' : avgScore >= 60 ? 'Satisfactory' : 'Needs Focus'}
          </h3>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        <div className="lg:col-span-1 bg-white border border-slate-100 rounded-[3.5rem] p-12 shadow-xl shadow-slate-100/50">
          <h3 className="font-black text-2xl text-slate-800 mb-10 flex items-center gap-3">
             Skill Matrix
          </h3>
          <div className="space-y-8">
            {SCORING_DOMAINS.map(domain => {
              const val = domainAverages[domain] || 0;
              return (
                <div key={domain}>
                  <div className="flex justify-between text-[10px] font-black mb-3 tracking-widest uppercase">
                    <span className="text-slate-500">{domain}</span>
                    <span className="text-blue-600">{val}%</span>
                  </div>
                  <div className="h-3 w-full bg-slate-50 rounded-full overflow-hidden border border-slate-100 shadow-inner">
                    <div 
                      className={`h-full rounded-full transition-all duration-1000 ${val > 80 ? 'bg-emerald-500 shadow-lg shadow-emerald-200' : val > 50 ? 'bg-blue-500 shadow-lg shadow-blue-200' : 'bg-amber-500 shadow-lg shadow-amber-200'}`} 
                      style={{ width: `${val}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-2 space-y-8">
           <div className="bg-amber-900 text-amber-50 p-12 rounded-[3.5rem] shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 p-10 opacity-5 text-9xl font-black italic">!</div>
              <h3 className="font-bold text-2xl mb-8 flex items-center gap-3">
                 Recurring Blind Spot Analysis
              </h3>
              <div className="space-y-4">
                 {topBlindSpots.length > 0 ? topBlindSpots.map(([spot, count], i) => (
                    <div key={i} className="bg-white/10 border border-white/20 px-6 py-4 rounded-2xl flex justify-between items-center group hover:bg-white/20 transition-all">
                       <span className="text-sm font-bold">{spot}</span>
                       <div className="flex items-center gap-2">
                          <span className="text-[10px] font-black bg-amber-500 text-amber-900 px-2 py-0.5 rounded uppercase">Detected {count}x</span>
                       </div>
                    </div>
                 )) : (
                    <p className="italic text-amber-200/50">No recurring blind spots identified. Continue simulations to build your error profile.</p>
                 )}
              </div>
              <p className="mt-8 text-xs text-amber-100/50 font-medium italic leading-relaxed">
                 "Critical pattern detection identifies omissions across multiple station domains. Addressing these 'recurring' gaps is the fastest way to move from novices to mastery."
              </p>
           </div>

           <div className="bg-white border border-slate-100 p-12 rounded-[3.5rem] shadow-xl shadow-slate-100/50 relative overflow-hidden group">
             <div className="flex justify-between items-center mb-10">
                <h3 className="font-black text-2xl text-slate-800 flex items-center gap-3">
                  Strategic Growth Plan
                </h3>
                {strategicPlan && (
                  <span className="text-[9px] font-black text-slate-300 uppercase tracking-widest">
                    Last Updated: {new Date(strategicPlan.updatedAt).toLocaleDateString()}
                  </span>
                )}
             </div>

             {updatingPlan ? (
                <div className="py-20 flex flex-col items-center justify-center">
                   <div className="w-12 h-12 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin mb-6"></div>
                   <p className="text-slate-400 font-bold text-xs uppercase tracking-widest">Recalibrating Plan based on latest session...</p>
                </div>
             ) : strategicPlan ? (
               <ul className="space-y-10">
                 {strategicPlan.items.map((item, idx) => (
                   <li key={idx} className="flex gap-8 group/item">
                     <div className="w-14 h-14 bg-blue-50 rounded-3xl flex items-center justify-center shrink-0 font-black text-blue-600 border-2 border-blue-100 group-hover/item:bg-blue-600 group-hover/item:text-white transition-all duration-300 shadow-lg">
                       {idx + 1}
                     </div>
                     <div>
                        <h4 className="text-blue-600 font-black text-sm uppercase tracking-widest mb-2">{item.title}</h4>
                        <p className="text-base text-slate-600 leading-relaxed font-medium">{item.advice}</p>
                     </div>
                   </li>
                 ))}
               </ul>
             ) : (
               <div className="py-20 text-center border-2 border-dashed border-slate-100 rounded-[3rem]">
                 <p className="text-slate-400 italic text-sm">Insufficient session history to generate a strategic plan. Finish a mock session to unlock personalized advice.</p>
                 {completedSessions.length > 0 && (
                   <button
                     onClick={onGeneratePlan}
                     className="mt-6 bg-slate-900 text-white px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-blue-600 transition-all shadow-lg"
                   >
                     Generate Plan Now
                   </button>
                 )}
               </div>
             )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Insights;
